import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ShoppingBag, ExternalLink, ArrowRight, Store, RefreshCw, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

function ProductCard({ p }) {
  return (
    <a
      href={p.product_url}
      target="_blank"
      rel="noreferrer"
      data-testid={`shop-product-${p.product_id}`}
      className="group block card-surface rounded-md overflow-hidden"
    >
      <div className="relative aspect-square overflow-hidden bg-charcoal">
        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {p.featured && (
          <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-gold text-charcoal font-bold">Featured</span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-lg font-semibold text-white leading-tight group-hover:text-gold transition-colors">{p.title}</h3>
        {p.description && <p className="mt-2 text-sm text-white/50 leading-relaxed line-clamp-2">{p.description}</p>}
        <div className="mt-4 flex items-center justify-between">
          {p.price ? <span className="font-mono text-gold font-semibold">{p.price}</span> : <span />}
          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-white/60 group-hover:text-gold transition-colors">
            View <ExternalLink className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </a>
  );
}

function AdminSyncNote({ count, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/refresh");
      toast.success(`Synced ${data.count} products from Shopify`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <section className="mb-10 rounded-md border border-gold/30 bg-gold/[0.04] p-5 sm:p-6" data-testid="shop-admin-sync">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-gold mt-0.5" />
          <div>
            <h2 className="label-mono">Products sync automatically from Shopify <span className="text-white/40">· only visible to you</span></h2>
            <p className="text-sm text-white/50 mt-1">
              This page pulls live from <span className="text-gold">shop.helpsolvethecase.com</span> ({count} item{count === 1 ? "" : "s"} showing).
              Add, edit or hide products in your Shopify admin — changes appear here within a few minutes.
              Tag a product <span className="text-gold font-mono">featured</span> in Shopify to pin it to the top.
            </p>
          </div>
        </div>
        <Button onClick={refresh} disabled={busy} data-testid="shop-refresh-btn" className="bg-gold text-charcoal hover:bg-gold-hover shrink-0">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Refresh now"}
        </Button>
      </div>
    </section>
  );
}

export default function ShopPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [storeUrl, setStoreUrl] = useState("");
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(() => {
    return api.get("/shop/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/shop/store").then(({ data }) => setStoreUrl(data.store_url)).catch(() => {});
    loadProducts();
  }, [loadProducts]);

  const publicProducts = products.filter((p) => p.active);
  const featured = publicProducts.filter((p) => p.featured);
  const rest = publicProducts.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet>
        <title>Shop — Help Solve The Case™</title>
        <meta name="description" content="Official Help Solve The Case merch and gear. Wear the mission — every purchase supports investigative work through the bullpen." />
        <link rel="canonical" href="https://helpsolvethecase.com/shop" />
        <meta property="og:title" content="Shop — Help Solve The Case™" />
        <meta property="og:description" content="Official Help Solve The Case merch and gear. Wear the mission." />
      </Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2"><ShoppingBag className="w-4 h-4 text-gold" /><span className="label-mono">The Shop</span></div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Wear the mission.</h1>
            <p className="mt-3 text-white/50 max-w-2xl leading-relaxed">Official Help Solve The Case gear. Every piece helps keep the bullpen going — and gets these cases in front of more people.</p>
          </div>
          {storeUrl && (
            <a href={storeUrl} target="_blank" rel="noreferrer" data-testid="visit-store-btn"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">
              <Store className="w-4 h-4" /> Visit the full store <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        {isAdmin && <AdminSyncNote count={publicProducts.length} onRefresh={loadProducts} />}

        {loading ? (
          <div className="label-mono animate-pulse py-20 text-center">Loading the shop…</div>
        ) : publicProducts.length === 0 ? (
          <div className="card-surface rounded-md p-12 text-center" data-testid="shop-empty-state">
            <ShoppingBag className="w-8 h-8 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">New gear is on the way. In the meantime, browse the full store.</p>
            {storeUrl && (
              <a href={storeUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-gold hover:text-gold-hover font-semibold text-sm">
                Visit the store <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <section className="mb-12">
                <h2 className="label-mono mb-4 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> Featured</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featured.map((p, i) => (
                    <motion.div key={p.product_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <ProductCard p={p} />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
            {rest.length > 0 && (
              <section>
                {featured.length > 0 && <h2 className="label-mono mb-4">More gear</h2>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rest.map((p, i) => (
                    <motion.div key={p.product_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                      <ProductCard p={p} />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
